// Malay (Bahasa Malaysia, "anda") copy for /web-uploader, translated from copy.en.ts. Same shape (checked by
// copy.test.ts). "Pemuat naik web" and "ruang kerja" as in the site's ms locale (src/i18n/locales/ms.json);
// "pratonton" and "fail asal" (the originals).
// The uploader's own labels are in English, so they're quoted as it shows them (“Previews”, “Originals”).
// The text inside the two animations stays in English, like the screenshots (the uploader is in English);
// their captions, legends and accessible names are translated.
import type { WebUploaderCopy } from "./copy.en";
import en from "./copy.en";

const ms: WebUploaderCopy = {
  seo: {
    title: "Pemuat naik web Photohawk | Lebih pantas daripada FTP",
    description:
      "Kami telah membina semula pemuat naik web Photohawk. Ia menghantar banyak foto serentak, mampu mengendalikan muat naik lebih daripada 100,000 foto dan bersambung semula dengan sendirinya jika sambungan anda terputus, pada semua pelan. Pada semua pelan, pratonton ringan dimuat naik dahulu supaya galeri anda dalam talian dalam beberapa minit, bukan berjam-jam.",
  },
  hero: {
    badge: "Baharu",
    eyebrow: "Pada semua pelan",
    title: "Pemuat naik web Photohawk yang baharu",
    subtitle:
      "Lebih pantas daripada FTP, dibina untuk muat naik lebih daripada 100,000 foto, dan ia menyambung semula dari tempat ia berhenti.",
    plans: "Pada semua pelan, dengan pratonton dahulu, acara besar boleh dalam talian dalam beberapa minit, bukan berjam-jam.",
    ctaPrimary: "Buka pemuat naik web",
    ctaSecondary: "Lihat apa yang baharu",
    screenshotAlt:
      "Pemuat naik web semasa muat naik sedang berjalan. Dengan pratonton dahulu (semua pelan), bar status menunjukkan galeri sudah dalam talian dengan pratonton dan berapa ramai orang yang sedang melawatinya, dan baris gilir di sebelah kanan menunjukkan pratonton yang sudah siap dan fail asal yang sedang dihantar, dengan dua foto yang dibeli didahulukan.",
    phoneAlt: "Pemuat naik web pada telefon, dibuka dalam folder galeri, dengan status muat naik di bahagian bawah.",
  },
  previews: {
    heading: "Pratonton dahulu",
    badge: "Semua pelan",
    subtitle:
      "Pada semua pelan: dalam talian dalam beberapa minit, bukan berjam-jam. Galeri 10,000 foto boleh berada dalam talian dan mula menjual dalam kira-kira 8 minit dengan kelajuan muat naik 20 Mbps.",
    body: "Pratonton ringan dimuat naik dahulu, jadi galeri anda boleh berada dalam talian dan mula menjual, atau memberikan muat turun percuma, sementara fail asal menyusul. Jika seseorang membeli atau memuat turun foto sebelum fail asalnya tiba, fail asal itu akan didahulukan dalam baris gilir.",
    steps: [
      { id: "previews", text: "Pratonton ringan dimuat naik dahulu" },
      { id: "live", text: "Galeri anda dalam talian" },
      { id: "buy", text: "Seseorang membeli foto" },
      { id: "jump", text: "Fail asalnya didahulukan dalam baris gilir" },
      { id: "originals", text: "Fail asal yang lain menyusul" },
    ],
    anim: {
      ...en.previews.anim,
      label: "Ilustrasi animasi pratonton dahulu",
      description:
        "Pratonton ringan enam foto terbang dari komputer anda ke galeri anda, dan galeri bertukar kepada “Live”. Seseorang mengetik “Buy” pada satu foto: fail asalnya bergerak ke depan baris gilir fail asal dan dimuat naik dahulu. Fail asal yang lain menyusul.",
    },
    link: "Cara muat naik pratonton dahulu berfungsi",
  },
  faster: {
    heading: "Mengapa ia lebih pantas daripada FTP",
    subtitle: "Foto yang sama, dihantar dengan dua cara.",
    anim: {
      ...en.faster.anim,
      label: "Perbandingan animasi antara aplikasi FTP dan pemuat naik web",
      description:
        "Dua baris. Di baris atas, aplikasi FTP menghantar foto 2 serentak secara lalai, dan tidak lebih daripada 10. Di baris bawah, pemuat naik web menghantar banyak foto serentak melalui lorong yang melebar dan menyempit mengikut sambungan. Pada semua pelan, pratonton ringan dihantar dahulu, jadi galerinya dalam talian lebih awal. Sambungan terputus seketika dan muat naik bersambung semula dengan sendirinya.",
      legendPreviews: "Pratonton",
      legendOriginals: "Fail asal",
    },
    points: [
      {
        id: "parallel",
        title: "Banyak foto serentak",
        body: "Ia menghantar banyak foto serentak, jauh lebih banyak daripada aplikasi FTP seperti FileZilla (2 serentak secara lalai, dan tidak lebih daripada 10).",
      },
      {
        id: "adaptive",
        title: "Menggunakan sambungan anda sepenuhnya",
        body: "Ia terus menyesuaikan diri semasa memuat naik, untuk menggunakan sambungan anda sepenuhnya.",
      },
      {
        id: "scale",
        title: "Dibina untuk acara besar",
        body: "Kami telah mengujinya pada skala besar, dan ia mampu mengendalikan muat naik lebih daripada 100,000 foto dengan mudah.",
      },
      {
        id: "live",
        title: "Dalam talian dalam beberapa minit, bukan berjam-jam (semua pelan)",
        body: "Pada semua pelan, pratonton ringan dimuat naik dahulu, jadi galeri 10,000 foto boleh berada dalam talian dan mula menjual dalam kira-kira 8 minit dengan kelajuan muat naik 20 Mbps, sementara fail asal menyusul.",
      },
      {
        id: "resume",
        title: "Menyambung dari tempat ia berhenti",
        body: "Jika sambungan anda terputus, ia bersambung semula dengan sendirinya. Tambah folder itu sekali lagi dan hanya yang tertinggal akan dihantar.",
      },
    ],
  },
  /** The upload-time estimator (#estimate). {placeholders} are filled in by the page. */
  estimate: {
    heading: "Berapa lama muat naik anda akan mengambil masa?",
    subtitle: "Anggaran untuk galeri anda, berdasarkan kelajuan muat naik anda sendiri.",
    heroLink: "Anggarkan masa muat naik anda",
    photos: "Bilangan foto",
    custom: "Lain-lain",
    customLabel: "Bilangan foto (lain-lain)",
    size: "Saiz purata satu fail asal",
    sizeUnit: "MB",
    sizeHint: "JPEG acara selalunya sekitar {mb} MB.",
    speed: "Kelajuan muat naik anda",
    speedUnit: "Mbps",
    test: "Uji kelajuan muat naik saya",
    testing: "Sedang menguji… {mbps} Mbps",
    testStarting: "Sedang menguji…",
    testDone: "Diukur: {mbps} Mbps muat naik",
    testFailed: "Kami tidak dapat mengukur kelajuan anda sekarang. Masukkan sendiri, atau cuba ujian kelajuan umum.",
    dataNote: "Menggunakan sehingga kira-kira {mb} MB data, dan hanya apabila anda mengetik butang.",
    otherTest: "Atau gunakan ujian kelajuan umum",
    resultsHeading: "Anggaran anda",
    previewsLabel: "Pratonton dahulu",
    previewsPlans: "Semua pelan",
    previewsLive: "Galeri dalam talian dalam {time}",
    previewsAll: "Semua fail asal dimuat naik dalam {time}",
    originalsLabel: "Fail asal sahaja",
    originalsLive: "Galeri dalam talian dalam {time}",
    originalsWhen: "apabila semua {gb} GB fail asal sudah dimuat naik",
    computerNote: "Pada kelajuan ini, kepantasan komputer anda membuat pratonton yang menentukan rentaknya.",
    disclaimer: "Anggaran sahaja. Masa sebenar bergantung pada sambungan, komputer dan foto anda.",
    time: {
      under: "kurang daripada seminit",
      min: "kira-kira {n} min",
      h: "kira-kira {n} jam",
      d: "kira-kira {n} hari",
    },
    table: {
      caption: "Contoh: 10,000 foto",
      speed: "Kelajuan muat naik",
      live: "Pratonton dahulu: galeri dalam talian (semua pelan)",
      all: "Semua fail asal dimuat naik",
      note: "Angka kira-kira 8 minit diukur pada kelajuan muat naik 20 Mbps (pratonton dahulu). Baris lain ialah anggaran daripada model yang sama, dengan fail asal kira-kira {mb} MB.",
    },
  },
  features: {
    heading: "Lagi yang baharu",
    subtitle: "Skrin sebenar daripada pemuat naik web yang baharu.",
    items: [
      {
        id: "status",
        title: "Status dalam bahasa yang mudah",
        body: "Lihat kedudukan semasa dengan sekali pandang. Dengan pratonton dahulu (semua pelan): “Previews” (pratonton), kemudian “Originals” (fail asal), masing-masing dengan kemajuannya sendiri.",
        alt: "Status muat naik dengan pratonton dahulu (semua pelan): “Live — saving originals…”, satu baris kemajuan untuk Previews dan satu untuk Originals, serta “2 photos people bought go first”.",
      },
      {
        id: "queue",
        title: "Baris gilir di mana sahaja anda mahu",
        body: "Lekatkan baris gilir muat naik di sebelah kanan, kiri atau bawah, dan seret mengikut saiz yang anda suka. Pelayar anda akan mengingati pilihan anda.",
        alt: "Baris gilir muat naik dilekatkan di bahagian bawah pemuat naik, dengan menunya dibuka untuk memindahkannya ke kanan, bawah atau kiri.",
      },
      {
        id: "visitors",
        title: "Lihat siapa yang sedang melihat",
        body: "Bar status menunjukkan berapa ramai orang yang sedang melihat galeri anda sekarang, pada semua pelan.",
        alt: "Bar status di bahagian atas pemuat naik: “Live on previews” (dengan pratonton dahulu) dan bilangan pelawat.",
      },
      {
        id: "resume",
        title: "Menyambung dari tempat ia berhenti",
        body: "Jika sambungan anda terputus, ia bersambung semula dengan sendirinya. Tambah folder itu sekali lagi dan hanya yang tertinggal akan dihantar.",
        alt: "Sepanduk dalam pemuat naik yang menyatakan muat naik sebuah folder belum selesai, dengan butang “Resume”.",
      },
      {
        id: "phone",
        title: "Jelas pada telefon juga",
        body: "Pada skrin kecil, laluan folder dilipat supaya muat, dan mengetik “…” menunjukkan folder di antaranya. Jadi anda sentiasa tahu ke mana foto anda dihantar.",
        alt: "Pemuat naik pada telefon: laluan folder menunjukkan folder semasa, dan “…” membuka senarai folder di atasnya.",
      },
    ],
  },
  ftp: {
    date: "1 November 2026",
    heading: "Apa yang berubah untuk FTP pada 1 November",
    body: "Pelayan FTP kongsi kami yang sentiasa aktif akan ditutup pada 1 November 2026. Menggunakan FTP sekarang? Sila beralih sebelum tarikh itu.",
    points: [
      {
        id: "closes",
        title: "FTP kongsi ditutup pada 1 November 2026",
        body: "Pelayan FTP kongsi yang sentiasa aktif akan dimatikan pada 1 November 2026.",
      },
      {
        id: "standard",
        title: "Pemuat naik web ialah cara standard untuk memuat naik",
        body: "Ia tersedia pada semua pelan, dan anda boleh menggunakannya hari ini.",
      },
      {
        id: "ondemand",
        title: "Jika FTP amat diperlukan",
        body: "Pada semua pelan, setiap ahli ruang kerja anda boleh memulakan pelayan FTP atas permintaan mereka sendiri daripada pemuat naik web, pada alamat peribadi yang tidak pernah berubah. Ia dimatikan selepas sejam tanpa aktiviti.",
      },
    ],
    helpLink: "Cara FTP atas permintaan berfungsi",
  },
  more: {
    previewFirstTitle: "Cara muat naik pratonton dahulu berfungsi",
    previewFirstBody: "Pada semua pelan. Langkah demi langkah: pratonton, galeri dalam talian, dan apa yang dilihat oleh pembeli.",
  },
  cta: {
    heading: "Cuba pada muat naik anda yang seterusnya",
    body: "Pemuat naik web yang baharu sudah sedia pada semua pelan. Tambah folder dan lihat galeri anda dalam talian.",
    button: "Buka pemuat naik web",
  },
  pause: "Jeda animasi",
  play: "Main animasi",
};

export default ms;
